import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Avatar,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  EmojiEvents as EmojiEventsIcon,
  CardGiftcard as CardGiftcardIcon,
  Chat as ChatIcon,
  Image as ImageIcon,
  Collections as CollectionsIcon,
  Public as PublicIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useLocalization } from '../hooks/useLocalization';
import axios from 'axios';

const drawerWidth = 280;

const ClientLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [bonusRequestsCount, setBonusRequestsCount] = useState(0);
  const [adminPanelAvailable, setAdminPanelAvailable] = useState(true);
  const [availabilityError, setAvailabilityError] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const { t } = useLocalization();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenTerms = () => {
    setTermsDialogOpen(true);
  };

  const handleCloseTerms = () => {
    setTermsDialogOpen(false);
  };

  const handleAgreeToTerms = () => {
    setAgreedToTerms(true);
    setTermsDialogOpen(false);
  };

  const checkAdminPanelAvailability = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/actions-availability/client-admin-panel-usage-available', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setAdminPanelAvailable(response.data.available);
      if (!response.data.available) {
        setAvailabilityError(response.data.error_message);
      }
    } catch (err) {
      console.error('Error checking admin panel availability:', err);
      setAdminPanelAvailable(false);
      setAvailabilityError('Failed to check panel availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const menuItems = [
    { 
      text: t('dashboard'), 
      icon: <DashboardIcon />, 
      path: '/client',
      description: t('dashboard_description'),
      alwaysAvailable: false
    },
    { 
      text: t('bonus_usage_requests'), 
      icon: (
        <Badge 
          badgeContent={bonusRequestsCount} 
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
              minWidth: '20px',
              height: '20px',
              borderRadius: '10px',
            }
          }}
        >
          <CheckCircleIcon />
        </Badge>
      ), 
      path: '/client/bonus-requests',
      description: t('bonus_usage_requests_description'),
      alwaysAvailable: false
    },
    { 
      text: t('users'), 
      icon: <PeopleIcon />, 
      path: '/client/clients',
      description: t('users_description'),
      alwaysAvailable: false
    },
    { 
      text: t('levels'), 
      icon: <EmojiEventsIcon />, 
      path: '/client/levels',
      description: t('levels_description'),
      alwaysAvailable: true
    },
    { 
      text: t('bonuses'), 
      icon: <CardGiftcardIcon />, 
      path: '/client/bonuses',
      description: t('bonuses_description'),
      alwaysAvailable: false
    },
    { 
      text: t('prompts'), 
      icon: <ChatIcon />, 
      path: '/client/prompts',
      description: t('prompts_description'),
      alwaysAvailable: true
    },
    { 
      text: t('planet_prompts'), 
      icon: <PublicIcon />, 
      path: '/client/planet-prompts',
      description: t('planet_prompts_description'),
      alwaysAvailable: true
    },
    { 
      text: t('generation_events'), 
      icon: <ImageIcon />, 
      path: '/client/generation-events',
      description: t('generation_events_description'),
      alwaysAvailable: false
    },
    { 
      text: t('attachments'), 
      icon: <CollectionsIcon />, 
      path: '/client/attachments',
      description: t('attachments_description'),
      alwaysAvailable: false
    },
    { 
      text: t('my_balance'), 
      icon: <AccountBalanceWalletIcon />, 
      path: '/client/my-balance',
      description: t('my_balance_description'),
      alwaysAvailable: false
    },
    { 
      text: t('client_settings'), 
      icon: <SettingsIcon />, 
      path: '/client/client-settings',
      description: t('client_settings_description'),
      alwaysAvailable: false
    },
  ];

  // Filter menu items based on availability
  const availableMenuItems = menuItems.filter(item => 
    item.alwaysAvailable || adminPanelAvailable
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 0
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mx: 'auto',
              mb: 2,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <BusinessIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('client_panel')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {t('integration_management')}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
        <List sx={{ p: 2 }}>
          {availableMenuItems.map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <Tooltip 
                key={item.text} 
                title={item.description} 
                placement="right"
                arrow
              >
                <ListItem
                  button
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }}
                  selected={isSelected}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.light' : 'action.hover',
                      transform: 'translateX(4px)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 40,
                      color: isSelected ? 'white' : 'primary.main'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 'bold' : 'normal',
                    }}
                  />
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        {/* Theme Toggle */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LightModeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('theme')}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={toggleTheme}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            }
            label=""
          />
          <DarkModeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        </Box>
        
        <Button
          fullWidth
          variant="outlined"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{
            borderRadius: 2,
            py: 1.5,
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.main',
              color: 'white',
              borderColor: 'error.main',
            },
          }}
        >
          {t('logout')}
        </Button>
      </Box>
    </Box>
  );

  useEffect(() => {
    const fetchBonusRequestsCount = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.get('/users/bonus_usage_requests', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setBonusRequestsCount(response.data.length);
      } catch (error) {
        console.error('Error fetching bonus usage requests count:', error);
        setBonusRequestsCount(0);
      }
    };

    fetchBonusRequestsCount();
    
    // Set up interval to refresh count every 30 seconds
    const interval = setInterval(fetchBonusRequestsCount, 30000);
    
    // Listen for custom event to refresh counter when bonuses are updated
    const handleBonusRequestUpdated = () => {
      fetchBonusRequestsCount();
    };
    
    window.addEventListener('bonusRequestUpdated', handleBonusRequestUpdated);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('bonusRequestUpdated', handleBonusRequestUpdated);
    };
  }, []);

  // Check admin panel availability on mount
  useEffect(() => {
    checkAdminPanelAvailability();
  }, []);

  // Listen for changes in prompts, planet prompts, and levels
  useEffect(() => {
    const handlePromptsChanged = () => {
      console.log('Prompts changed, re-checking availability...');
      checkAdminPanelAvailability();
    };

    const handlePlanetPromptsChanged = () => {
      console.log('Planet prompts changed, re-checking availability...');
      checkAdminPanelAvailability();
    };

    const handleLevelsChanged = () => {
      console.log('Levels changed, re-checking availability...');
      checkAdminPanelAvailability();
    };

    // Add event listeners
    window.addEventListener('promptsChanged', handlePromptsChanged);
    window.addEventListener('planetPromptsChanged', handlePlanetPromptsChanged);
    window.addEventListener('levelsChanged', handleLevelsChanged);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('promptsChanged', handlePromptsChanged);
      window.removeEventListener('planetPromptsChanged', handlePlanetPromptsChanged);
      window.removeEventListener('levelsChanged', handleLevelsChanged);
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {availableMenuItems.find(item => item.path === location.pathname)?.text || t('client_panel')}
            </Typography>
            <Chip
              label={t('integration_client')}
              size="small"
              sx={{ 
                ml: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`} arrow>
              <IconButton
                onClick={toggleTheme}
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <BusinessIcon sx={{ fontSize: 18 }} />
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: (theme) => theme.zIndex.drawer,
        }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              position: 'fixed',
              height: '100vh',
              border: 'none',
              boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, p: 3 }}>
          {/* Show error message if admin panel is not available */}
          {!checkingAvailability && !adminPanelAvailable && (
            <Box
              sx={{
                backgroundColor: 'error.light',
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: 2,
                p: 3,
                mb: 3,
                color: 'error.contrastText',
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                {t('client_admin_panel_usage_unavailable')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('please_ensure_client_has')}
              </Typography>
              <Box component="ul" sx={{ textAlign: 'left', display: 'inline-block', mb: 2 }}>
                <Typography component="li" variant="body2">
                  {t('at_least_one_prompt')}
                </Typography>
                <Typography component="li" variant="body2">
                  {t('at_least_one_planet_prompt')}
                </Typography>
                <Typography component="li" variant="body2">
                  {t('level_with_zero_points')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {availabilityError}
              </Typography>
            </Box>
          )}
          
          <Outlet />
        </Box>
        
        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 3,
            background: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            © 2024 Perkflow tech
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={handleOpenTerms}
            sx={{ 
              textDecoration: 'none', 
              cursor: 'pointer',
              color: 'primary.main',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Пользовательское соглашение
          </Link>
        </Box>
      </Box>

      {/* Terms of Service Dialog */}
      <Dialog
        open={termsDialogOpen}
        onClose={handleCloseTerms}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '80vh',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          Публичная оферта
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ whiteSpace: 'pre-line' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Публичная оферта
            </Typography>
            <Typography variant="body2" paragraph>
              Индивидуального предпринимателя Реверчука Захара Игоревича
              ОГРНИП: 324180000055050, ИНН: 183113272251
            </Typography>
            <Typography variant="body2" paragraph>
              Настоящий документ является официальным предложением (публичной офертой) ИП Реверчук Захар Игоревич, в дальнейшем именуемого "Исполнитель", любому физическому лицу, в дальнейшем именуемому "Пользователь", заключить договор на условиях, изложенных ниже.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              1. Предмет соглашения
            </Typography>
            <Typography variant="body2" paragraph>
              1.1. Исполнитель предоставляет Пользователю доступ к цифровым токенам ("токены") на платформе Perkflow.
            </Typography>
            <Typography variant="body2" paragraph>
              1.2. Токены могут быть использованы Пользователем исключительно в рамках платформы для доступа к функциональности генерации уникальных изображений, элементов игрового мира и других персонализированных функций.
            </Typography>
            <Typography variant="body2" paragraph>
              1.3. Оплата токенов осуществляется на основании настоящего соглашения, через интерфейс сайта.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              2. Условия оплаты
            </Typography>
            <Typography variant="body2" paragraph>
              2.1. Стоимость токенов указывается на сайте в момент покупки. Оплата производится в рублях или иной доступной валюте с использованием платёжных средств, указанных на сайте.
            </Typography>
            <Typography variant="body2" paragraph>
              2.2. После подтверждения оплаты токены зачисляются на внутренний баланс Пользователя.
            </Typography>
            <Typography variant="body2" paragraph>
              2.3. Оплата считается подтверждённой после поступления средств на расчётный счёт Исполнителя.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              3. Возврат средств
            </Typography>
            <Typography variant="body2" paragraph>
              3.1. Возврат средств возможен только в случае технической ошибки, в результате которой токены не были зачислены Пользователю.
            </Typography>
            <Typography variant="body2" paragraph>
              3.2. Возврат осуществляется на основании письменного обращения Пользователя с приложением подтверждающих документов. Срок рассмотрения заявки — до 10 рабочих дней.
            </Typography>
            <Typography variant="body2" paragraph>
              3.3. Частичный или полный возврат оплаченных токенов по инициативе Пользователя не предусмотрен.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              4. Использование токенов
            </Typography>
            <Typography variant="body2" paragraph>
              4.1. Токены являются цифровыми единицами, не имеющими номинальной стоимости вне платформы Perkflow и не подлежат обмену на денежные средства.
            </Typography>
            <Typography variant="body2" paragraph>
              4.2. Токены не подлежат передаче третьим лицам.
            </Typography>
            <Typography variant="body2" paragraph>
              4.3. Срок действия токенов — не ограничен, за исключением случаев прекращения работы сервиса. В случае закрытия сервиса Пользователям будет предложено использовать токены в течение 30 дней.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              5. Ответственность
            </Typography>
            <Typography variant="body2" paragraph>
              5.1. Исполнитель не несёт ответственности за невозможность использования токенов по причинам, не зависящим от него (например, технические сбои на стороне Пользователя, ограничение доступа к интернету и т.п.).
            </Typography>
            <Typography variant="body2" paragraph>
              5.2. Исполнитель вправе вносить изменения в функциональность, правила использования токенов и условия настоящей оферты, размещая обновления на сайте.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              6. Принятие оферты
            </Typography>
            <Typography variant="body2" paragraph>
              6.1. Совершение Пользователем оплаты токенов является полным и безоговорочным принятием условий настоящей оферты.
            </Typography>
            <Typography variant="body2" paragraph>
              6.2. С момента оплаты токенов договор между Пользователем и Исполнителем считается заключённым.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
              7. Реквизиты исполнителя:
            </Typography>
            <Typography variant="body2" paragraph>
              ИП Реверчук Захар Игоревич
              ОГРНИП: 324180000055050
              ИНН: 183113272251
              Электронная почта: zakhar@reverch.uk
              Юридический адрес: Москва, Мосфильмовская улица, д. 40
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseTerms}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Закрыть
          </Button>
          <Button 
            onClick={handleAgreeToTerms} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Я согласен с условиями оферты
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientLayout; 